import { SxProps, Theme, alpha, createTheme, lighten } from '@mui/material/styles';
import {
  BIRDZ_COLORS,
  BIRDZ_RADIUS,
  withBirdzDesignSystem
} from '../../../../styles/birdzDesignSystem';

/**
 * Orange de la charte pour les puces qui réclament une action : « Incomplète » à la
 * qualification, « À affecter » à l'affectation.
 *
 * `tertiary` n'est pas exposé dans la palette MUI — c'est une décision testée — et son
 * commentaire dans le design system sanctionne l'import direct pour ce cas. Seul le fond
 * est posé : le texte foncé par défaut du Chip est le seul contraste lisible dessus.
 *
 * Éclairci pour rejoindre le niveau pastel des autres puces : à l'orange brut de la charte,
 * il était le seul jeton sombre et saturé de l'écran et écrasait ses voisines. L'action à
 * mener reste signalée par la teinte, seule chaude de la série, plus que par l'intensité.
 *
 * Le taux est dosé pour atterrir à 79 % de clarté, comme « Pré-remplie » : les deux puces
 * adoucies de la colonne Qualification tiennent le même niveau, juste au-dessus des deux
 * qui gardent leur jeton brut. Toucher à l'une sans l'autre romprait cet alignement.
 */
export const ACTION_NEEDED_CHIP_SX = { bgcolor: lighten(BIRDZ_COLORS.tertiary, 0.25) };

/**
 * Puce « Pré-remplie » : une demande que l'import a remplie sans que personne ne l'ait
 * qualifiée. Le vert de « Qualifiée », éclairci.
 *
 * Volontairement la même teinte que l'état suivant plutôt qu'une quatrième couleur : les
 * deux puces se lisent alors comme une progression, la pâle disant « presque » et la
 * pleine « c'est fait ». Dérivée du même jeton, pour qu'elles ne puissent pas diverger.
 * L'écart de clarté est franc, donc lisible aussi en vision déficiente, et le libellé
 * distingue les deux de toute façon. Contraste d'environ 14,5:1 avec le texte foncé par
 * défaut du Chip.
 *
 * Le taux est dosé pour atterrir à 79 % de clarté, comme « Incomplète » — voir le
 * commentaire de `ACTION_NEEDED_CHIP_SX`.
 */
export const PREFILLED_CHIP_SX = { bgcolor: lighten(BIRDZ_COLORS.secondary, 0.4) };

/**
 * Puce « Nouveau » : une demande issue du dernier ajout, en bleu glacier.
 *
 * Dérivée du bleu clair de la charte plutôt que posée en dur, pour que les deux suivent la même
 * source si elle bouge. Très éclaircie, elle reste distincte d'`info` — la puce « À qualifier »,
 * affichée sur la même ligne — par la clarté et non par la teinte, ce qui tient aussi en vision
 * déficiente. Le texte foncé par défaut du Chip passe largement AA sur ce fond (environ 15:1).
 */
export const NEW_CHIP_SX = { bgcolor: lighten(BIRDZ_COLORS.info, 0.55) };

/**
 * Fond d'une ligne cochée, dans les deux tableaux.
 *
 * La v1 obtenait cet effet par `BTable.scss`, qui décline la ligne `.selected` en une
 * teinte par thème — `custom-blue` à `#f7f7ff` sur fond blanc, soit environ 6 % de
 * l'accent, et un liseré à environ 18 %. On reprend ces proportions plutôt que les six
 * couples de valeurs en dur : `accent` porte déjà la couleur du thème courant.
 *
 * Volontairement pas la prop `selected` de MUI : elle impose le gris `action.selected`,
 * qu'il faudrait ensuite combattre en spécificité.
 */
export function selectedRowSx(accent: string): SxProps<Theme> {
  return {
    backgroundColor: alpha(accent, 0.06),
    '& td': { borderBottomColor: alpha(accent, 0.18) },
    // Le survol reste perceptible sur une ligne déjà teintée, comme en v1.
    '&:hover': { backgroundColor: alpha(accent, 0.11) }
  };
}

/** Épaisseur du liseré gris autour des pastilles d'un contrôle segmenté. */
const SEGMENTED_TRACK_PADDING = 4;

/**
 * Thème de l'écran de création d'intervention : le design system Birdz, plus les
 * deux contrôles propres à la maquette de la refonte.
 *
 * Ces deux contrôles — onglets en pilules et contrôle segmenté — ne figurent pas
 * dans la charte graphique, qui ne montre que selects, recherche, cases à cocher et
 * bouton de filtres. Ils restent donc ici plutôt que dans `birdzDesignSystem.ts`, et
 * gardent le rayon pilule alors que la charte impose 20px aux boutons.
 */
export function createInterventionCreationTheme(parentTheme: Partial<Theme>): Theme {
  const base = withBirdzDesignSystem(parentTheme);

  return createTheme(base, {
    components: {
      /*
       * Modales de confirmation. `BirdzDialog` de la BRL rend un `Dialog` MUI nu : titre
       * en noir, et deux boutons texte de même poids où rien ne distingue la confirmation
       * de l'abandon. On le rapproche de la modale d'ajout de points de service, seule
       * modale de l'écran déjà mise en forme — le rayon et le halo du paper, eux, viennent
       * déjà du design system.
       */
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            // La charte veut l'accent de l'utilisateur sur les titres de modale, et
            // `ConfirmDialog` le pose — mais il ne vit que dans le contexte global, hors
            // de portée d'un thème statique. Le navy reste donc ici comme doublure, pour
            // toute modale qui ne passerait pas par ce composant.
            color: base.palette.primary.main
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: 16,
            gap: 8,
            /*
             * `BirdzDialog` pose ces deux identifiants sur ses boutons, et n'accepte
             * qu'une chaîne comme titre : c'est le seul point de prise pour leur donner
             * une hiérarchie sans réécrire ses options sur les trois écrans qui
             * l'appellent. Si la BRL les renommait, on retomberait sur ses boutons texte
             * d'origine — dégradation visuelle, pas de casse.
             */
            '& #cancel_button': {
              color: base.palette.text.secondary
            },
            '& #validation_button': {
              backgroundColor: base.palette.primary.main,
              color: base.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: base.palette.primary.dark
              }
            }
          }
        }
      },
      // Onglets en pilules : conteneur gris arrondi, onglet actif en pastille pleine.
      // MUI utilise un soulignement par défaut, on masque donc son indicateur.
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 'auto',
            display: 'inline-flex',
            padding: SEGMENTED_TRACK_PADDING,
            borderRadius: BIRDZ_RADIUS.pill,
            backgroundColor: base.palette.grey[200]
          },
          flexContainer: {
            gap: SEGMENTED_TRACK_PADDING
          },
          indicator: {
            display: 'none'
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 'auto',
            minWidth: 'auto',
            padding: '6px 18px',
            borderRadius: BIRDZ_RADIUS.pill,
            textTransform: 'none',
            fontWeight: 500,
            color: base.palette.text.secondary,
            '&.Mui-selected': {
              backgroundColor: base.palette.primary.main,
              color: base.palette.primary.contrastText
            }
          }
        }
      },
      // Contrôle segmenté : piste grise, pastille active blanche.
      // MUI met à plat les coins internes et intercale un liseré entre les boutons
      // (marginLeft: -1 + borderLeft) via ses slots first/middle/lastButton :
      // on rétablit le rayon dans ces mêmes slots pour rester à specificité égale.
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: BIRDZ_RADIUS.pill,
            padding: SEGMENTED_TRACK_PADDING,
            gap: SEGMENTED_TRACK_PADDING,
            backgroundColor: base.palette.grey[200]
          },
          grouped: {
            border: 0
          },
          firstButton: {
            borderRadius: BIRDZ_RADIUS.pill
          },
          middleButton: {
            borderRadius: BIRDZ_RADIUS.pill,
            marginLeft: 0,
            borderLeft: 0
          },
          lastButton: {
            borderRadius: BIRDZ_RADIUS.pill,
            marginLeft: 0,
            borderLeft: 0
          }
        }
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            border: 0,
            borderRadius: BIRDZ_RADIUS.pill,
            padding: '6px 18px',
            textTransform: 'none',
            fontWeight: 500,
            color: base.palette.text.secondary,
            '&.Mui-selected': {
              backgroundColor: base.palette.common.white,
              color: base.palette.text.primary,
              boxShadow: base.shadows[1],
              '&:hover': {
                backgroundColor: base.palette.common.white
              }
            }
          }
        }
      }
    }
  });
}
